import { Controller } from "@hotwired/stimulus"

// In-App Tutorial System Controller
// Provides step-by-step guided tours for complex features
export default class extends Controller {
  static targets = ["overlay", "tooltip", "spotlight", "progress", "stepContent", "stepNumber", "totalSteps"]
  static values = {
    tutorialId: String,
    steps: Array,
    currentStep: { type: Number, default: 0 },
    saveUrl: String
  }

  connect() {
    // Check if this tutorial has been completed
    const completedTutorials = this.getCompletedTutorials()
    if (completedTutorials.includes(this.tutorialIdValue)) {
      // Tutorial already completed, don't show it
      return
    }

    // Check if user dismissed this tutorial
    const dismissedTutorials = this.getDismissedTutorials()
    if (dismissedTutorials.includes(this.tutorialIdValue)) {
      // Tutorial was dismissed, don't show it
      return
    }

    // Start tutorial after a short delay
    setTimeout(() => this.startTutorial(), 500)
  }

  disconnect() {
    // Clean up
    this.removeTutorialElements()
  }

  startTutorial() {
    if (this.stepsValue.length === 0) {
      console.warn('No tutorial steps defined')
      return
    }

    // Show overlay
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('hidden')
    }

    // Show first step
    this.currentStepValue = 0
    this.showStep(0)
  }

  showStep(stepIndex) {
    if (stepIndex < 0 || stepIndex >= this.stepsValue.length) {
      return
    }

    const step = this.stepsValue[stepIndex]

    // Update step content
    if (this.hasStepContentTarget) {
      this.stepContentTarget.innerHTML = this.renderStepContent(step)
    }

    // Update progress
    this.updateProgress(stepIndex)

    // Position tooltip and spotlight
    this.positionTutorialElements(step)
  }

  renderStepContent(step) {
    return `
      <div class="tutorial-step-header">
        <h3 class="text-lg font-bold text-gray-900">${step.title}</h3>
        ${step.subtitle ? `<p class="text-sm text-gray-600 mt-1">${step.subtitle}</p>` : ''}
      </div>
      <div class="tutorial-step-body mt-4">
        <p class="text-gray-700">${step.content}</p>
        ${step.tips ? `
          <div class="tutorial-tip mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <div class="flex gap-2">
              <svg class="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p class="text-sm text-indigo-900">${step.tips}</p>
            </div>
          </div>
        ` : ''}
      </div>
    `
  }

  positionTutorialElements(step) {
    if (!step.target) {
      // No target element, center the tooltip
      this.positionCentered()
      return
    }

    // Find target element
    const targetElement = document.querySelector(step.target)
    if (!targetElement) {
      console.warn(`Tutorial target not found: ${step.target}`)
      this.positionCentered()
      return
    }

    // Position spotlight
    this.positionSpotlight(targetElement, step.spotlightPadding || 10)

    // Position tooltip
    this.positionTooltip(targetElement, step.placement || 'bottom')

    // Scroll target into view
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  positionSpotlight(targetElement, padding) {
    if (!this.hasSpotlightTarget) return

    const rect = targetElement.getBoundingClientRect()
    const spotlight = this.spotlightTarget

    spotlight.style.top = `${rect.top - padding + window.scrollY}px`
    spotlight.style.left = `${rect.left - padding}px`
    spotlight.style.width = `${rect.width + padding * 2}px`
    spotlight.style.height = `${rect.height + padding * 2}px`
    spotlight.classList.remove('hidden')
  }

  positionTooltip(targetElement, placement) {
    if (!this.hasTooltipTarget) return

    const rect = targetElement.getBoundingClientRect()
    const tooltip = this.tooltipTarget
    const tooltipRect = tooltip.getBoundingClientRect()

    let top, left

    switch (placement) {
      case 'top':
        top = rect.top - tooltipRect.height - 20 + window.scrollY
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2)
        break
      case 'bottom':
        top = rect.bottom + 20 + window.scrollY
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2)
        break
      case 'left':
        top = rect.top + (rect.height / 2) - (tooltipRect.height / 2) + window.scrollY
        left = rect.left - tooltipRect.width - 20
        break
      case 'right':
        top = rect.top + (rect.height / 2) - (tooltipRect.height / 2) + window.scrollY
        left = rect.right + 20
        break
      default:
        top = rect.bottom + 20 + window.scrollY
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2)
    }

    // Ensure tooltip stays within viewport
    const maxLeft = window.innerWidth - tooltipRect.width - 20
    const maxTop = window.innerHeight - tooltipRect.height - 20

    left = Math.max(20, Math.min(left, maxLeft))
    top = Math.max(20 + window.scrollY, Math.min(top, maxTop + window.scrollY))

    tooltip.style.top = `${top}px`
    tooltip.style.left = `${left}px`
    tooltip.classList.remove('hidden')
  }

  positionCentered() {
    if (!this.hasTooltipTarget) return

    const tooltip = this.tooltipTarget
    tooltip.style.top = '50%'
    tooltip.style.left = '50%'
    tooltip.style.transform = 'translate(-50%, -50%)'
    tooltip.classList.remove('hidden')

    if (this.hasSpotlightTarget) {
      this.spotlightTarget.classList.add('hidden')
    }
  }

  updateProgress(stepIndex) {
    const totalSteps = this.stepsValue.length
    const currentStep = stepIndex + 1

    if (this.hasStepNumberTarget) {
      this.stepNumberTarget.textContent = currentStep
    }

    if (this.hasTotalStepsTarget) {
      this.totalStepsTarget.textContent = totalSteps
    }

    if (this.hasProgressTarget) {
      const percentage = (currentStep / totalSteps) * 100
      this.progressTarget.style.width = `${percentage}%`
    }
  }

  nextStep() {
    if (this.currentStepValue < this.stepsValue.length - 1) {
      this.currentStepValue++
      this.showStep(this.currentStepValue)
    } else {
      // Tutorial completed
      this.completeTutorial()
    }
  }

  previousStep() {
    if (this.currentStepValue > 0) {
      this.currentStepValue--
      this.showStep(this.currentStepValue)
    }
  }

  skipTutorial() {
    // Mark as dismissed
    this.dismissTutorial()
    this.closeTutorial()
  }

  async completeTutorial() {
    // Mark tutorial as completed
    const completedTutorials = this.getCompletedTutorials()
    completedTutorials.push(this.tutorialIdValue)
    localStorage.setItem('completedTutorials', JSON.stringify(completedTutorials))

    // Save to server if URL provided
    if (this.saveUrlValue) {
      await this.saveTutorialCompletion()
    }

    // Show completion message
    this.showCompletionMessage()

    // Close after delay
    setTimeout(() => this.closeTutorial(), 2000)
  }

  dismissTutorial() {
    const dismissedTutorials = this.getDismissedTutorials()
    dismissedTutorials.push(this.tutorialIdValue)
    localStorage.setItem('dismissedTutorials', JSON.stringify(dismissedTutorials))
  }

  closeTutorial() {
    this.removeTutorialElements()
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add('hidden')
    }
  }

  removeTutorialElements() {
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.add('hidden')
    }
    if (this.hasSpotlightTarget) {
      this.spotlightTarget.classList.add('hidden')
    }
  }

  showCompletionMessage() {
    if (!this.hasStepContentTarget) return

    this.stepContentTarget.innerHTML = `
      <div class="text-center py-8">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <svg class="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Tutorial Complete!</h3>
        <p class="text-gray-600">You're all set. Happy gaming!</p>
      </div>
    `
  }

  async saveTutorialCompletion() {
    try {
      await fetch(this.saveUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          tutorial_id: this.tutorialIdValue,
          completed: true
        })
      })
    } catch (error) {
      console.error('Failed to save tutorial completion:', error)
    }
  }

  getCompletedTutorials() {
    try {
      return JSON.parse(localStorage.getItem('completedTutorials') || '[]')
    } catch {
      return []
    }
  }

  getDismissedTutorials() {
    try {
      return JSON.parse(localStorage.getItem('dismissedTutorials') || '[]')
    } catch {
      return []
    }
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }

  // Handle keyboard navigation
  handleKeydown(event) {
    switch(event.key) {
      case 'ArrowRight':
      case 'Enter':
        this.nextStep()
        break
      case 'ArrowLeft':
        this.previousStep()
        break
      case 'Escape':
        this.skipTutorial()
        break
    }
  }
}
